/**
 * The CSV upload preview (spec 05-TBL §5.3). A functional review surface (final visual
 * design is UX-owed): the row-level error list (with Apply disabled and the "nothing is
 * written until every error is fixed" copy), a distinct out-of-package WARNING block, the
 * change / revert / ignored counts line, and Apply — enabled only on a clean report with
 * something to write. Apply re-validates client-side on click (owned by the parent), so a
 * report gone stale against the working log can never apply (all-or-nothing).
 */

import {
  InlineNotification,
  ListItem,
  Modal,
  UnorderedList,
} from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import type { CsvValidationReport } from './types';
import styles from './csv-preview-modal.module.scss';

export interface CsvPreviewModalProps {
  open: boolean;
  /** The uploaded file name (heading context). */
  fileName: string | null;
  /** A file-level parse error (missing Slot ID column / unreadable file) — no report renders. */
  fileError: string | null;
  /** The validation report, or null when a file-level error preempts it. */
  report: CsvValidationReport | null;
  /** Applies the validated writes (the parent re-validates first). */
  onApply: () => void;
  /** Closes the preview without applying. */
  onClose: () => void;
}

/**
 * Renders the upload preview modal.
 *
 * @param {CsvPreviewModalProps} props - Open state, file context, the report, and the apply/close handlers.
 * @return {JSX.Element | null} The modal, or null when closed.
 */
export function CsvPreviewModal({
  open,
  fileName,
  fileError,
  report,
  onApply,
  onClose,
}: CsvPreviewModalProps) {
  const hasErrors = (report?.rowErrors.length ?? 0) > 0;
  const writeCount =
    (report?.changedDateCount ?? 0) + (report?.blankCellRevertCount ?? 0);
  const canApply = !fileError && !hasErrors && writeCount > 0;

  return (
    <Modal
      open={open}
      size="md"
      modalHeading="Review uploaded schedule"
      modalLabel={fileName ?? undefined}
      primaryButtonText="Apply"
      secondaryButtonText="Cancel"
      primaryButtonDisabled={!canApply}
      onRequestSubmit={onApply}
      onRequestClose={onClose}
    >
      {fileError && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Could not read this file"
          subtitle={fileError}
          className={styles.notification}
        />
      )}

      {report && (
        <>
          {hasErrors && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="Fix these rows before applying"
              subtitle="Nothing will be written until every error is fixed."
              className={styles.notification}
            />
          )}

          {hasErrors && (
            <UnorderedList className={styles.errorList}>
              {report.rowErrors.map((error) => (
                <ListItem key={`${error.rowNumber}-${error.kind}`}>
                  Row <FormattedCount value={error.rowNumber} /> ·{' '}
                  {error.slotId ?? '—'} — {error.message}
                </ListItem>
              ))}
            </UnorderedList>
          )}

          {report.unmatchedRows.length > 0 && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title="Some rows name Properties not in this evaluation"
              subtitle={`They will be ignored — the rest still applies.`}
              className={styles.notification}
            />
          )}

          {report.unmatchedRows.length > 0 && (
            <UnorderedList className={styles.warningList}>
              {report.unmatchedRows.map((unmatched) => (
                <ListItem key={unmatched.rowNumber}>
                  Row <FormattedCount value={unmatched.rowNumber} /> ·{' '}
                  {unmatched.slotId}
                </ListItem>
              ))}
            </UnorderedList>
          )}

          <p className={styles.counts} aria-live="polite">
            <FormattedCount value={report.changedDateCount} /> dates change ·{' '}
            <FormattedCount value={report.blankCellRevertCount} /> revert to
            modeled · <FormattedCount value={report.unmatchedRows.length} />{' '}
            rows ignored
          </p>

          {!hasErrors && writeCount === 0 && !fileError && (
            <p className={styles.noChanges}>
              This file matches the current schedule — there is nothing to
              apply.
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
